import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { SortBy } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import type {
  AdvancedSearchResultType,
  SearchParameters,
  SearchSortField,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';

import { searchActions } from 'app/views/layout/views/search/search.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import {
  convertSortObjectToString,
  convertSortStringToObject,
} from '../../../../utils/sort/convertSort';

type SearchResultsSortProps = PropsWithClassName;

type SearchResultsSortOption = {
  value: SortBy<SearchSortField> | '';
  label: string;
  tabs: string[];
};

export const sortOptions: SearchResultsSortOption[] = [
  {
    value: '',
    label: 'Relevance',
    tabs: ['conversations', 'notes'],
  },
  {
    value: 'updated_at:desc',
    label: 'Activity: Newest first',
    tabs: ['conversations'],
  },
  {
    value: 'updated_at:asc',
    label: 'Activity: Oldest first',
    tabs: ['conversations'],
  },
  {
    value: 'created_at:desc',
    label: 'Created: Newest first',
    tabs: ['conversations', 'notes'],
  },
  {
    value: 'created_at:asc',
    label: 'Created: Oldest first',
    tabs: ['conversations', 'notes'],
  },
  {
    value: 'last_message_created_at:desc',
    label: 'Last message: Newest first',
    tabs: ['conversations'],
  },
  {
    value: 'last_message_created_at:asc',
    label: 'Last message: Oldest first',
    tabs: ['conversations'],
  },
];

/**
 * Represents a component that is used to select a sorting option from a
 * dropdown
 */
export const SearchResultsSort = ({
  className,
}: SearchResultsSortProps): JSX.Element | null => {
  const { params } = useAppSelector((state) => state.search);
  const dispatch = useAppDispatch();
  const { tab } = useAppPathParams('/search/:tab');

  if (tab === 'contacts') return null;

  const value = convertSortObjectToString(params.sort);
  const selectedOption = sortOptions.find((option) => value === option.value);

  function handleToggleSortBy(value: SearchResultsSortOption['value']) {
    const nextParams: SearchParameters = {
      sort: convertSortStringToObject(value),
      page: 1,
    };

    dispatch(searchActions.params.update(nextParams));

    ampli.searchSortingUpdated({
      tab: tab as AdvancedSearchResultType,
      type: !value ? 'relevance' : value,
    });
  }

  return (
    <FlexBox className={className} alignItems="center">
      <Text.Paragraph variant="3" subdued>
        Sort by:
      </Text.Paragraph>

      <DropdownButton
        buttonProps={{ variant: 'tertiary' }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <ButtonList>
                {sortOptions
                  .filter((option) =>
                    option.tabs.includes(tab as AdvancedSearchResultType),
                  )
                  .map((option) => (
                    <ButtonList.Item
                      key={option.value}
                      isSelected={value === option.value}
                      onClick={() => {
                        if (value === option.value) return;
                        toggleDropdown();
                        handleToggleSortBy(option.value);
                      }}
                    >
                      {option.label}
                    </ButtonList.Item>
                  ))}
              </ButtonList>
            )}
          </DropdownButtonContext.Consumer>
        }
        fixed
      >
        {selectedOption?.label}
      </DropdownButton>
    </FlexBox>
  );
};
